import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ExternalLink, Copy, KeyRound, Send, Unplug, RefreshCw } from "lucide-react";

const inputCls = "w-full border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/30";
const labelCls = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60";
const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors disabled:opacity-50";

function StatusLine({ status }) {
  if (status.connected) {
    return (
      <div data-testid="gmail-status-connected" className="flex items-center gap-2 text-xs text-[#166534] bg-[#166534]/5 border border-[#166534]/20 rounded-md px-3 py-2">
        <CheckCircle2 className="h-4 w-4" /> Connected — emails send from <strong>{status.email}</strong>
      </div>
    );
  }
  return (
    <div data-testid="gmail-status-disconnected" className="flex items-center gap-2 text-xs text-[#7a5a1a] bg-[#FFF9C4] border border-[#FFCB05]/50 rounded-md px-3 py-2">
      <AlertTriangle className="h-4 w-4" />
      {status.configured ? "Keys saved — click “Connect Gmail” and sign in as the sender account." : "Paste your Google Client ID + Secret below, then click Save."}
    </div>
  );
}

export function GmailSetup() {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({ client_id: "", client_secret: "", sender: "" });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback(() => {
    api.get("/admin/gmail-status").then(({ data }) => setStatus(data)).catch((e) => setLoadErr(formatApiError(e)));
    api.get("/admin/gmail-config").then(({ data }) => {
      setCfg(data);
      setF((x) => ({ client_id: x.client_id || data.client_id || "", client_secret: x.client_secret, sender: x.sender || data.sender || "info@avidahealth.org" }));
    }).catch((e) => setLoadErr(formatApiError(e)));
  }, []);
  useEffect(() => {
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load]);

  if (loadErr && (!status || !cfg)) {
    return <div data-testid="gmail-setup-error" className="text-xs text-[#b91c1c] bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded-md px-3 py-2">Couldn't load Gmail settings: {loadErr}</div>;
  }
  if (!status || !cfg) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/gmail-config", f); toast.success("Gmail keys saved — now click Connect Gmail"); setF((x) => ({ ...x, client_secret: "" })); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect the Gmail account? Emails will stop sending until you connect again.")) return;
    try { await api.delete("/admin/gmail-connection"); toast.success("Gmail disconnected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const sendTest = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/gmail-test", { to: testTo.trim() }); toast.success(`Test email sent to ${data.sent_to}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard?.writeText(cfg.redirect_uri).then(() => toast.success("Copied"));

  return (
    <div data-testid="gmail-setup" className="bg-white border border-[#15233B]/10 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-[#1E73C9]" />
        <div className="text-sm font-extrabold text-[#15233B]">Gmail sending</div>
        <button data-testid="gmail-refresh" onClick={load} title="Refresh status" className="ml-auto p-1 rounded hover:bg-[#15233B]/5"><RefreshCw className="h-3.5 w-3.5 text-[#15233B]/50" /></button>
      </div>
      <StatusLine status={status} />
      {cfg.problems?.length > 0 && (
        <div data-testid="gmail-config-problems" className="text-xs text-[#b91c1c] bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded-md px-3 py-2 space-y-1">
          {cfg.problems.map((p) => <div key={p} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{p}</div>)}
        </div>
      )}
      {!status.connected && (
        <details data-testid="gmail-setup-help" className="text-xs text-[#15233B]/70">
          <summary className="cursor-pointer font-bold text-[#1E73C9]">How to set this up (Google Cloud Console)</summary>
          <ol className="list-decimal ml-5 mt-2 space-y-1">
            <li>APIs &amp; Services → Library → enable <strong>Gmail API</strong>.</li>
            <li>OAuth consent screen → User type <strong>Internal</strong>.</li>
            <li>Credentials → your <strong>OAuth client ID (Web application)</strong> → add the redirect URI shown below → Save.</li>
            <li>Paste the Client ID + Secret here → <strong>Save keys</strong> → <strong>Connect Gmail</strong> → sign in as the sender account.</li>
          </ol>
        </details>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Client ID</label>
          <input data-testid="gmail-client-id" value={f.client_id} onChange={(e) => set("client_id", e.target.value)} placeholder="1234…apps.googleusercontent.com" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Client Secret</label>
          <input data-testid="gmail-client-secret" type="password" autoComplete="new-password" value={f.client_secret} onChange={(e) => set("client_secret", e.target.value)}
            placeholder={cfg.has_secret ? `Saved${cfg.secret_hint ? ` (ends ${cfg.secret_hint})` : ""} — leave blank to keep` : "GOCSPX-…"} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Send emails from</label>
          <input data-testid="gmail-sender" type="email" value={f.sender} onChange={(e) => set("sender", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Authorized redirect URI (add in Google Cloud)</label>
          <div className="flex gap-1.5">
            <input data-testid="gmail-redirect-uri" readOnly value={cfg.redirect_uri} className={`${inputCls} bg-[#F6F3EC] font-mono text-xs`} />
            <button data-testid="gmail-redirect-copy" type="button" onClick={copy} className="px-2 rounded border border-[#15233B]/15 hover:bg-[#15233B]/5"><Copy className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="gmail-config-save" onClick={save} disabled={busy || !f.client_id.trim()} className={`${btn} bg-[#1E73C9] text-white hover:bg-[#15233B]`}>Save keys</button>
        {status.configured && (
          <a data-testid="gmail-connect-link" href={status.connect_url} target="_blank" rel="noreferrer" className={`${btn} bg-[#FFCB05] text-[#15233B] hover:bg-[#FBC02D]`}>
            {status.connected ? "Reconnect Gmail" : "Connect Gmail"} <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {status.connected && (
          <button data-testid="gmail-disconnect" onClick={disconnect} className={`${btn} border border-[#15233B]/15 text-[#15233B]/60 hover:text-[#b91c1c]`}><Unplug className="h-3.5 w-3.5" /> Disconnect</button>
        )}
        {cfg.source === "env" && <span className="text-[10px] text-[#15233B]/45">Currently using keys from the server's .env file</span>}
      </div>

      {status.connected && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[#15233B]/10">
          <input data-testid="gmail-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send a test email to…" className={`${inputCls} max-w-xs`} />
          <button data-testid="gmail-test-send" onClick={sendTest} disabled={busy || !testTo.includes("@")} className={`${btn} border border-[#1E73C9]/40 text-[#1E73C9] hover:bg-[#1E73C9]/10`}><Send className="h-3.5 w-3.5" /> Send test</button>
        </div>
      )}
    </div>
  );
}
